<template>
    <div class="participant-input-wrapper">
        <div class="participants-container" ref="participantsContainer">
            <!-- Display existing participants -->
            <div class="participant-item" v-for="(participant, index) in participants" :key="index">
                <span class="participant-email">{{ participant }}</span>
                <button @click="removeParticipant(index)" class="remove-btn" type="button">
                    &times;
                </button>
            </div>

            <!-- Input for adding new participants -->
            <input ref="emailInput" type="email" class="email-input" v-model="emailInput"
                placeholder="Add participant email..." @keypress.enter="addParticipant" @keyup="validateEmail"
                @blur="addParticipant" />
        </div>

        <!-- Add button -->
        <button type="button" class="add-btn" @click="addParticipant" :disabled="!isValidEmail || !emailInput.trim()">
            Add
        </button>

        <!-- Error message -->
        <div v-if="errorMessage" class="error-message">
            {{ errorMessage }}
        </div>
    </div>
</template>

<script>
export default {
    name: "ParticipantInput",
    props: {
        value: {
            type: Array,
            default: () => []
        },
        placeholder: {
            type: String,
            default: "Add participant email..."
        },
        maxParticipants: {
            type: Number,
            default: 100
        }
    },
    data() {
        return {
            participants: [],
            emailInput: "",
            isValidEmail: false,
            errorMessage: ""
        };
    },
    watch: {
        value: {
            handler(newVal) {
                this.participants = [...newVal];
            },
            immediate: true
        }
    },
    methods: {
        validateEmail() {
            const email = this.emailInput.trim();
            if (!email) {
                this.isValidEmail = false;
                this.errorMessage = "";
                return;
            }

            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            this.isValidEmail = emailRegex.test(email);

            if (!this.isValidEmail) {
                this.errorMessage = "Please enter a valid email address";
            } else if (this.participants.includes(email)) {
                this.isValidEmail = false;
                this.errorMessage = "This email is already added";
            } else {
                this.errorMessage = "";
            }
        },

        addParticipant() {
            const email = this.emailInput.trim();

            if (!email) return;

            this.validateEmail();

            if (this.isValidEmail && !this.participants.includes(email)) {
                if (this.participants.length >= this.maxParticipants) {
                    this.errorMessage = `Maximum ${this.maxParticipants} participants allowed`;
                    return;
                }

                this.participants.push(email);
                this.emailInput = "";
                this.isValidEmail = false;
                this.errorMessage = "";

                this.$emit('input', this.participants);
                this.$emit('change', this.participants);

                // Focus back to input for easy multiple additions
                this.$nextTick(() => {
                    this.$refs.emailInput.focus();
                });
            }
        },

        removeParticipant(index) {
            this.participants.splice(index, 1);
            this.errorMessage = "";
        },

        clearAll() {
            this.participants = [];
            this.emailInput = "";
            this.errorMessage = "";
        }
    }
};
</script>

<style lang="scss" scoped>
.participant-input-wrapper {
    .participants-container {
        border: 1px solid #ddd;
        background: #fff;
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        padding: 8px;
        border-radius: 5px;
        min-height: 40px;
        gap: 5px;

        .participant-item {
            display: flex;
            align-items: center;
            background: #707070;
            border-radius: 15px;
            padding: 4px 8px;
            font-size: 12px;
            color: white;
            margin: 2px;

            .participant-email {
                margin-right: 8px;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
                max-width: 200px;
            }

            .remove-btn {
                background: rgba(255, 255, 255, 0.2);
                color: white;
                border: none;
                border-radius: 50%;
                width: 18px;
                height: 18px;
                font-size: 14px;
                cursor: pointer;
                display: flex;
                align-items: center;
                justify-content: center;
                transition: background-color 0.2s;

                &:hover {
                    background: rgba(255, 255, 255, 0.3);
                }
            }
        }

        .email-input {
            border: none;
            outline: none;
            padding: 5px;
            font-size: 14px;
            flex: 1;
            min-width: 200px;

            &::placeholder {
                color: #999;
            }
        }
    }

    .add-btn {
        margin-top: 8px;
        padding: 6px 12px;
        background: #04a5a5;
        color: white;
        border: none;
        border-radius: 4px;
        cursor: pointer;
        font-size: 12px;
        transition: background-color 0.2s;

        &:hover:not(:disabled) {
            background: #038888;
        }

        &:disabled {
            background: #ccc;
            cursor: not-allowed;
        }
    }

    .error-message {
        color: #e74c3c;
        font-size: 12px;
        margin-top: 5px;
    }
}
</style>